import * as vp from 'vopay';
import { useMemo } from 'react';
import { format, isSameYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import styles from './AccountingSystemTotals.module.scss';

type AccountingSystemTotalsProps = {
  billsOrInvoices: vp.BillOrInvoiceRailz[];
  type: 'Payable' | 'Receivable';
  activeTab: 'open' | 'closed';
  // Currency the zero amount renders in when no rows match the filters.
  emptyStateCurrency: string;
  // Which date the closed tab is anchored on — shown after the range
  // ("Selected duration + by {date type}").
  dateTypeLabel?: string;
  startDate?: vp.Timestamp;
  endDate?: vp.Timestamp;
};

export default function AccountingSystemTotals({
  billsOrInvoices,
  type,
  activeTab,
  emptyStateCurrency,
  dateTypeLabel,
  startDate,
  endDate,
}: AccountingSystemTotalsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.TotalsHeader' });

  const hasDateRange = !!startDate && !!endDate;

  // Open counts what is still due; closed counts what was settled.
  const totals = useMemo(() => {
    const totalByCurrency: { [currencyCode: string]: number } = {};

    billsOrInvoices.forEach((row) => {
      const amount = Number(vp.sanitizeToNumeric(String(activeTab === 'open' ? row.amountDue : row.totalAmount))) || 0;
      totalByCurrency[row.currency || ''] = (totalByCurrency[row.currency || ''] || 0) + amount;
    });

    // No matching rows still shows an explicit zero, not a bare label.
    if (!Object.keys(totalByCurrency).length) totalByCurrency[emptyStateCurrency] = 0;

    return totalByCurrency;
  }, [billsOrInvoices, activeTab, emptyStateCurrency]);

  // No total for an all-time range on either tab ("For All Time on Closed, we
  // do not show Total" — Figma; on the open tab the aging header's Total Owed
  // (All-Time) already is that figure).
  if (!hasDateRange) return null;

  const rangeStart = new Date(Number(startDate));
  const rangeEnd = new Date(Number(endDate));

  // The year is shown once when both ends share it ("Aug 16 – Sep 14, 2026"),
  // on both ends when the range straddles a year boundary.
  const range = `${format(rangeStart, isSameYear(rangeStart, rangeEnd) ? 'MMM d' : 'MMM d, yyyy')} – ${format(rangeEnd, 'MMM d, yyyy')}`;

  const label =
    activeTab === 'open'
      ? t('totalDue', { defaultValue: 'Total Due ({{range}})', range })
      : type === 'Receivable'
        ? t('totalReceived', {
            defaultValue: 'Total Received ({{range}}, by {{dateType}})',
            range,
            dateType: dateTypeLabel,
          })
        : t('totalPaid', { defaultValue: 'Total Paid ({{range}}, by {{dateType}})', range, dateType: dateTypeLabel });

  return (
    <div className={styles.container} data-testid="accounting-totals">
      <div className={styles.label}>{label}</div>

      <div className={styles.amounts}>
        {Object.entries(totals).map(([currencyCode, amount], index) => (
          <span key={currencyCode} className={styles.amount}>
            {index > 0 && <span className={styles.amountDivider}>|</span>}
            {`${currencyCode} ${vp.formattedCurrency(amount.toFixed(2))}`}
          </span>
        ))}
      </div>
    </div>
  );
}
